import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { GoogleButton } from '../../components/GoogleButton.jsx';
import { ProviderButton, signInStartUrl } from '../../components/SignInButtons.jsx';
import { Alert, Badge, Button, SectionTitle } from '../../components/ui.jsx';

const LABELS = { google: 'Google', facebook: 'Facebook', instagram: 'Instagram' };

/**
 * How this account signs in: a password, Google, Facebook, Instagram — add
 * one, remove one, never the last. Adding Instagram here is also the only way
 * to use it with an account made another way, since Instagram shares no email
 * to match it by.
 */
export default function Account() {
  const { user, isBrand, refresh, googleEnabled, facebookEnabled, instagramEnabled } = useAuth();
  const [params, setParams] = useSearchParams();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');

  // Back from Facebook or Instagram: what happened, said once.
  useEffect(() => {
    const added = params.get('added');
    if (added && LABELS[added]) {
      setNotice(`${LABELS[added]} added. You can sign in with it from now on.`);
      refresh().catch(() => {});
    }
    if (params.get('failed') === '1') {
      api
        .get('/auth/oauth/status')
        .then((s) => s?.error && setError(s.error))
        .catch(() => {});
    }
    if (added || params.get('failed')) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const methods = user?.signIn ?? {};
  const count = Object.values(methods).filter(Boolean).length;

  async function remove(method) {
    setBusy(method);
    setError('');
    setNotice('');
    try {
      await api.delete(`/auth/signin/${method}`);
      await refresh();
      setNotice(`${LABELS[method]} removed.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function emailPasswordLink() {
    setBusy('password');
    setError('');
    try {
      await api.post('/auth/forgot', { email: user.email });
      setNotice(`We emailed ${user.email} a link to ${methods.password ? 'change' : 'set'} your password. It lasts an hour.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  // Stable, or Google's button would be drawn again on every render.
  const addGoogle = useCallback(
    async (credential) => {
      const { user: u } = await api.post('/auth/google/link', { credential });
      await refresh();
      setNotice('Google added. You can sign in with it from now on.');
      return u;
    },
    [refresh],
  );

  async function setEmails(notifyByEmail) {
    setBusy('emails');
    setError('');
    setNotice('');
    try {
      await api.patch('/auth/preferences', { notifyByEmail });
      await refresh();
      setNotice(notifyByEmail ? 'We’ll email you as well.' : 'No more of these emails. The bell still shows them.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  const available = { google: googleEnabled, facebook: facebookEnabled, instagram: instagramEnabled };

  return (
    <DashboardLayout side={isBrand ? 'brand' : 'creator'} title="Account" description={`Signed in as ${user?.email}`}>
      <section className="max-w-xl space-y-4">
        <SectionTitle hint="Use any of these to sign in. You can’t remove the last one.">How you sign in</SectionTitle>
        <Alert>{error}</Alert>
        <Alert tone="positive">{notice}</Alert>

        <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-raised">
          <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <p className="text-sm font-medium">Email and password</p>
              <p className="text-xs text-ink-muted">
                {user?.email}
                {!user?.emailVerified && ' · not confirmed yet'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {methods.password ? <Badge tone="positive">on</Badge> : <Badge>no password</Badge>}
              <Button size="sm" variant="outline" onClick={emailPasswordLink} disabled={busy === 'password'}>
                {methods.password ? 'Change password' : 'Set a password'}
              </Button>
            </div>
          </li>

          {Object.keys(LABELS).map((method) => {
            const has = Boolean(methods[method]);
            if (!has && !available[method]) return null;
            return (
              <li key={method} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{LABELS[method]}</p>
                  {method === 'instagram' && (
                    <p className="text-xs text-ink-muted">Business or creator accounts</p>
                  )}
                </div>
                {has ? (
                  <div className="flex items-center gap-2">
                    <Badge tone="positive">on</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(method)}
                      disabled={busy === method || count <= 1}
                      title={count <= 1 ? 'Your only way to sign in' : undefined}
                    >
                      Remove
                    </Button>
                  </div>
                ) : method === 'google' ? (
                  <GoogleButton onCredential={addGoogle} onError={setError} divider={false} />
                ) : (
                  <div className="w-full sm:w-auto">
                    <ProviderButton provider={method} href={signInStartUrl(method, { link: true })} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="mt-10 max-w-xl space-y-4">
        <SectionTitle hint="Everything also shows in the bell at the top of the dashboard.">Notifications</SectionTitle>
        <label className="flex items-start gap-3 rounded-lg border border-rule bg-paper-raised px-4 py-3">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-accent"
            checked={user?.notifyByEmail !== false}
            disabled={busy === 'emails'}
            onChange={(e) => setEmails(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium">Email me too</span>
            <span className="block text-xs text-ink-muted">
              Gift requests, applications, invitations, posts, pitches and payouts. A failed card payment is always
              emailed, and so are password and sign-in emails.
            </span>
          </span>
        </label>
      </section>
    </DashboardLayout>
  );
}
